import React from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AccountShell } from "./components/AccountShell";
import { AccountAuthProvider, useAccountAuth } from "./contexts/AccountAuthContext";
import { accountPreviewEnabled } from "./dev/preview";
import {
  BillingCancelPage,
  BillingPage,
  BillingSuccessPage,
} from "./pages/BillingPage";
import { DownloadsPage } from "./pages/DownloadsPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { HomePage } from "./pages/HomePage";
import { LoginPage } from "./pages/LoginPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { SecurityPage } from "./pages/SecurityPage";
import "@cp/ui/styles/index.css";
import "./styles/global.css";
import "./styles/portal.css";
import "./styles/plans.css";

function EnablePreview() {
  accountPreviewEnabled();
  const params = new URLSearchParams(window.location.search);
  const plans = params.get("plans") === "1";
  const next = params.get("next");
  const target = next && next.startsWith("/") ? next : plans ? "/billing" : "/";
  const search = plans ? "?plans=1" : next ? "" : "";
  return <Navigate to={`${target}${search}`} replace />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { loggedIn, ready } = useAccountAuth();
  const location = useLocation();
  if (!ready) return <p className="account-hint">Sitzung wird geprüft …</p>;
  if (!loggedIn) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <AccountAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/vorschau/*" element={<EnablePreview />} />
          <Route path="/vorschau" element={<EnablePreview />} />
          <Route element={<AccountShell />}>
            <Route
              index
              element={
                <RequireAuth>
                  <HomePage />
                </RequireAuth>
              }
            />
            <Route
              path="billing"
              element={
                <RequireAuth>
                  <BillingPage />
                </RequireAuth>
              }
            />
            <Route
              path="billing/success"
              element={
                <RequireAuth>
                  <BillingSuccessPage />
                </RequireAuth>
              }
            />
            <Route
              path="billing/cancel"
              element={
                <RequireAuth>
                  <BillingCancelPage />
                </RequireAuth>
              }
            />
            <Route
              path="downloads"
              element={
                <RequireAuth>
                  <DownloadsPage />
                </RequireAuth>
              }
            />
            <Route
              path="security"
              element={
                <RequireAuth>
                  <SecurityPage />
                </RequireAuth>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AccountAuthProvider>
  );
}
